"use client";

import { AudioExampleModule } from "@/components/AudioExampleModule";
import { Modal } from "@/components/Modal";
import { Heading3 } from "@/components/text/Heading3";
import { Heading4 } from "@/components/text/Heading4";
import { NormalText } from "@/components/text/NormalText";
import { useConversionGuideModal } from "@/lib/zustand/useConversionGuideModal";

export function ConversionGuideModal() {
  const conversionGuideModalOpen = useConversionGuideModal(
    (state) => state.open
  );
  const setConversionGuideModalClose = useConversionGuideModal(
    (state) => state.setClose
  );

  return (
    <Modal
      open={conversionGuideModalOpen}
      closeModal={setConversionGuideModalClose}
      title="Voice conversion guide"
      widthMode="wide"
    >
      <NormalText className="mb-1" elementType="p">
        Although our AI voice conversion technology is robust, there are several
        brief suggestions you can follow to maximize the quality of your
        outcomes.
      </NormalText>
      <NormalText className="mb-1" elementType="p">
        Keep your input audio clean and dry (no reverb, background noise, delay,
        chorus, excessive compression or heavy autotone).
      </NormalText>
      <NormalText elementType="p">
        If you&apos;re struggling to ge a high quality conversion, try to find a
        royalty-free voice with a vocal style that is similar to your input
        vocals.
      </NormalText>
      <AudioExampleModule
        heading="Good input (no effects)"
        leftTitle="Before"
        leftDescription="No reverb, no delay, mono, no noise"
        leftAudioSource={"audio/examples/Good-Conversion-Input.mp3"}
        rightTitle="After"
        rightDescription="Clean, no AI artifacts"
        rightAudioSource={"audio/examples/Good-Conversion-Output.mp3"}
      />

      <AudioExampleModule
        heading="Bad input (effects)"
        leftTitle="Before"
        leftDescription="Reverb, delay, harmonies, stereo, heavy compression"
        leftAudioSource={
          "audio/examples/Bad-Conversion-Reverb,-Delay,-Stereo,-Hard-Compression-Input.mp3"
        }
        rightTitle="After"
        rightDescription="Unclear, AI artifacts"
        rightAudioSource={
          "audio/examples/Bad-Conversion-Reverb,-Delay,-Stereo,-Hard-Compression-Output.mp3"
        }
      />

      <AudioExampleModule
        heading="Bad input (background noise)"
        leftTitle="Before"
        leftDescription="Background noise"
        leftAudioSource={
          "audio/examples/Bad-Conversion-Background-Noise-Input.mp3"
        }
        rightTitle="After"
        rightDescription="AI artifacts"
        rightAudioSource={
          "audio/examples/Bad-Conversion-Background-Noise-Output.mp3"
        }
      />

      <AudioExampleModule
        heading="Good input (style match)"
        leftTitle="Before"
        leftDescription="Female pop vocal"
        leftAudioSource={"audio/examples/Style-Match-Input.mp3"}
        rightTitle="After"
        rightDescription="Female Pop Vocal"
        rightAudioSource={"audio/examples/Style-Match-Output.mp3"}
      />

      <AudioExampleModule
        heading="Bad input (style mismatch)"
        leftTitle="Before"
        leftDescription="female pop vocal"
        leftAudioSource={"audio/examples/Style-Mismatch-Input-Female-Pop.mp3"}
        rightTitle="After"
        rightDescription="Male Rap Vocal"
        rightAudioSource={
          "audio/examples/Style-Mismatch-Rap-Male-Output-Trying-On-A-Female-Pop-Song.mp3"
        }
      />

      <Heading3 elementType="h3" className="mt-6">
        Advanced tips
      </Heading3>

      <Heading4 elementType="h3" className="mb-2 mt-4">
        Export your input audio as true mono
      </Heading4>
      <NormalText elementType="p">
        A stereo input file, even when the left and right channels are
        identical, can introduce distortions in the higher frequencies.
      </NormalText>

      <Heading4 elementType="h3" className="mb-2 mt-4">
        Pre-process your input
      </Heading4>
      <NormalText elementType="p">
        Apply a noise gate to eliminate background noise, employ high and low
        pass filters to eliminate unwanted frequencies, and use gentle
        compression to manage the dynamics. Refrain from using tuning on your
        input vocals and avoid excessive compression.
      </NormalText>

      <Heading4 elementType="h3" className="mb-2 mt-4">
        Post-process your output.
      </Heading4>
      <NormalText elementType="p">
        Bring the vocals into your DAW and put it through your own effects rack.
      </NormalText>
    </Modal>
  );
}
